/**
 * Elimină fundalul unei imagini (ex. logo JPEG cu fundal alb/solid) și întoarce un data-URL PNG
 * cu fundal transparent. Procesare 100% client-side (canvas) — nu modifică nimic în DB/backend.
 *
 * Flood-fill pornit din marginile imaginii: doar pixelii conectați de margine, similari ca
 * culoare cu vecinul deja marcat transparent, devin transparenți. Zonele albe/deschise DIN
 * interiorul logo-ului (ex. interiorul literelor) nu sunt atinse, fiindcă nu sunt conectate
 * la margine printr-un lanț de pixeli asemănători.
 */
export function removeBackgroundToPngDataUrl(imageUrl: string, tolerance = 26): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const { naturalWidth: width, naturalHeight: height } = img;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(imageUrl); return; }

        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, width, height);
        const data = imageData.data;
        const visited = new Uint8Array(width * height);
        const tol2 = tolerance * tolerance;

        const similar = (i: number, j: number) => {
          const dr = data[i] - data[j];
          const dg = data[i + 1] - data[j + 1];
          const db = data[i + 2] - data[j + 2];
          return dr * dr + dg * dg + db * db <= tol2;
        };

        const stack: number[] = [];
        const pushIfEdgePixel = (x: number, y: number) => {
          const idx = y * width + x;
          if (!visited[idx]) { visited[idx] = 1; stack.push(x, y); }
        };
        for (let x = 0; x < width; x++) { pushIfEdgePixel(x, 0); pushIfEdgePixel(x, height - 1); }
        for (let y = 0; y < height; y++) { pushIfEdgePixel(0, y); pushIfEdgePixel(width - 1, y); }

        while (stack.length) {
          const y = stack.pop()!;
          const x = stack.pop()!;
          const idx = (y * width + x) * 4;
          data[idx + 3] = 0;

          const neighbors = [[x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]];
          for (const [nx, ny] of neighbors) {
            if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
            const nIdx = ny * width + nx;
            if (visited[nIdx]) continue;
            if (similar(idx, nIdx * 4)) {
              visited[nIdx] = 1;
              stack.push(nx, ny);
            }
          }
        }

        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(imageUrl);
      }
    };
    img.onerror = () => resolve(imageUrl);
    img.src = imageUrl;
  });
}
